/**
 * Règle de visibilité / partage des paramètres personnalisés (santé + environnement) — côté web.
 *
 * Un paramètre personnalisé porte un mode de visibilité explicite :
 *   - `visibility: 'zoo'`     → visible par tout le parc (comportement historique)
 *   - `visibility: 'shared'`  → visible par le créateur + les userIds listés dans `sharedWith`
 *   - `visibility: 'private'` → visible par le créateur (+ admins) uniquement
 *
 * Le champ `createdBy` (déjà présent sur les documents) désigne le propriétaire : il voit et
 * édite toujours son paramètre.
 *
 * Rétro-compatibilité : les anciens paramètres ne portent pas `visibility`. Pour eux, on
 * retombe sur `'zoo'` (tout le parc les voyait déjà) → aucun changement de visibilité, pas de
 * migration Firestore nécessaire.
 *
 * Gouvernance : seuls les rôles « privilégiés » peuvent CRÉER (admin / superAdmin / manager /
 * vétérinaire). L'ÉDITION / l'ARCHIVAGE est réservé au créateur + admins (admin / superAdmin /
 * manager) + staff AKONGO. Comme on ne peut éditer que ce qu'on voit, les admins voient aussi
 * tous les paramètres (y compris `private`) ; le vétérinaire, lui, peut créer mais ne voit que
 * le parc + les siens + ceux partagés avec lui.
 *
 * ⚠️ POINT DE VÉRITÉ UNIQUE : toute lecture de paramètres personnalisés DOIT décider de la
 * visibilité via ce helper. Ne jamais réintroduire de check inline, sous peine de laisser
 * fuiter un paramètre privé/partagé (l'enforcement Firestore est impossible ici, cf. règles en
 * OU logique — le filtrage côté client est la seule barrière).
 */

/** Rôles autorisés à CRÉER un paramètre personnalisé. */
export const PRIVILEGED_CREATE_ROLES = ["admin", "superAdmin", "manager", "veterinary"];

/** Rôles qui administrent (voient tout + éditent/archivent tout), en plus du créateur et du staff AKONGO. */
export const ADMIN_ROLES = ["admin", "superAdmin", "manager"];

/** Modes de visibilité valides. */
export const VISIBILITY_MODES = ["private", "shared", "zoo"];

/**
 * Visibilité effective d'un paramètre. Un champ absent ou invalide est traité comme `'zoo'`
 * (legacy : tout le parc).
 */
export function effectiveVisibility(param) {
  const v = param && param.visibility;
  return v === "private" || v === "shared" || v === "zoo" ? v : "zoo";
}

/**
 * Normalise un objet `userData` (formats variés rencontrés dans l'app, éventuellement wrappé
 * dans `{ data }` par `_getUserData`) vers la forme `viewer` attendue par les fonctions ci-dessous.
 */
export function viewerFromUserData(userData = {}) {
  const d = (userData && userData.data) || userData || {};
  return {
    userId: d.userId || d.uid || d.id || null,
    userRoleValue: (d.userRight && d.userRight.value) || d.userRight || d.role || null,
    isAkongoStaff: d.akongoStaff === true,
    isVet: (d.rights && d.rights.vet === true) || (d.userRights && d.userRights.vet === true),
    // Secteurs de l'utilisateur (appartenance = sectorKeeper), mis en cache au login.
    sectorIds: Array.isArray(d.sectorsIds) ? d.sectorsIds : [],
  };
}

/**
 * Construit un `viewer` depuis le `localStorage` (clé `userData`, alimentée par
 * `UserDatabaseConnector._getUserData`). Permet aux connecteurs (qui n'ont pas le `userData`
 * en contexte) de filtrer sans changer leur signature. Échoue de façon sûre (rien de visible
 * au-delà du parc) si indisponible.
 */
export function viewerFromLocalStorage() {
  try {
    const raw =
      typeof localStorage !== "undefined" ? localStorage.getItem("userData") : null;
    return viewerFromUserData(raw ? JSON.parse(raw) : {});
  } catch (e) {
    return {};
  }
}

/** Le viewer est-il vétérinaire (par rôle ou par droit `rights.vet`) ? */
function isVetViewer(viewer = {}) {
  return viewer.isVet === true || viewer.userRoleValue === "veterinary";
}

/**
 * Cet utilisateur peut-il CRÉER un paramètre personnalisé ?
 * → staff AKONGO, ou rôle ∈ {admin, superAdmin, manager, veterinary}.
 */
export function canCreateParameter(viewer = {}) {
  if (viewer.isAkongoStaff) return true;
  if (isVetViewer(viewer)) return true;
  return PRIVILEGED_CREATE_ROLES.includes(viewer.userRoleValue);
}

/**
 * Cet utilisateur peut-il ÉDITER / ARCHIVER ce paramètre ?
 * → créateur, staff AKONGO, ou rôle ∈ {admin, superAdmin, manager}.
 */
export function canEditParameter(param, viewer = {}) {
  if (!viewer) return false;
  if (viewer.userId && param && param.createdBy && param.createdBy === viewer.userId) {
    return true;
  }
  if (viewer.isAkongoStaff) return true;
  return ADMIN_ROLES.includes(viewer.userRoleValue);
}

/**
 * Rôles d'un secteur qui en font un membre : toutes les listes de rôles du
 * secteur (retours ICM du 12/10, ticket 02). Miroir du mobile
 * (WellfareMonitor2 app/utils/sectors/sectorMembership.js, SECTOR_ROLE_FIELDS).
 */
export const SECTOR_MEMBER_FIELDS = [
  "sectorKeeper",
  "managementUserIds",
  "veterinaryUserIds",
  "nutritionUserIds",
  "technicalUserIds",
  "zoologicalUserIds",
  "trainingUserIds",
];

/** Secteurs du parc dont `userId` est membre, par l'un des rôles ci-dessus. */
export function memberSectorIds(sectors, userId, zooId = null) {
  if (!userId || !Array.isArray(sectors)) return [];
  const ids = [];
  sectors.forEach((sector) => {
    if (!sector || (zooId && sector.zooId && sector.zooId !== zooId)) return;
    const member = SECTOR_MEMBER_FIELDS.some(
      (field) => Array.isArray(sector[field]) && sector[field].includes(userId)
    );
    const id = sector.sectorId || sector.id || sector.value;
    if (member && id && !ids.includes(id)) ids.push(id);
  });
  return ids;
}

// Appartenance en direct du compte connecté, tenue par liveSectorMembership.js
// (écoute des secteurs du parc). Null tant qu'elle n'est pas connue.
let liveMembership = null;

export function setLiveSectorMembership(userId, sectorIds) {
  liveMembership = userId && Array.isArray(sectorIds) ? { userId, sectorIds: [...sectorIds] } : null;
}

export function viewerSectorIds(viewer = {}) {
  const uid = viewer && viewer.userId;
  if (liveMembership && uid && liveMembership.userId === uid) return liveMembership.sectorIds;
  return (viewer && viewer.sectorIds) || [];
}

/**
 * Cet utilisateur peut-il VOIR / UTILISER ce paramètre ?
 * → parc (zoo/legacy), ou administrateur/créateur (peut tout voir pour administrer), ou
 *   destinataire explicite quand `visibility === 'shared'`.
 */
export function canSeeParameter(param, viewer = {}) {
  if (!param) return false;
  const vis = effectiveVisibility(param);
  if (vis === "zoo") return true;
  // Créateur + admins + staff voient tout (cohérence : on ne peut éditer que ce qu'on voit).
  if (canEditParameter(param, viewer)) return true;
  if (vis === "shared") {
    const uid = viewer && viewer.userId;
    if (Array.isArray(param.sharedWith) && !!uid && param.sharedWith.includes(uid)) {
      return true;
    }
    // Partage par secteur : visible si l'utilisateur appartient à l'un des secteurs ciblés.
    // Appartenance lue EN DIRECT (liveSectorMembership) quand elle est connue pour ce
    // compte, sinon celle du viewer (cache de connexion) — recette 12.1.0 SAN-22.
    const viewerSectors = viewerSectorIds(viewer);
    return (
      Array.isArray(param.sharedSectors) &&
      viewerSectors.some((s) => param.sharedSectors.includes(s))
    );
  }
  return false; // private, ni créateur ni admin
}

/** Filtre une liste de paramètres pour ne garder que ceux visibles par ce viewer. */
export function filterVisibleParameters(params, viewer) {
  if (!Array.isArray(params)) return [];
  return params.filter((p) => canSeeParameter(p, viewer));
}
